import { useState, useEffect, useContext } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import Folder from '@mui/icons-material/Folder';
import { IconButton, CircularProgress } from '@mui/material';
import UnifiedModal from './UnifiedModal';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import BasicMenu from './BasicMenu';
import EmptyState from './EmptyState';
import LinkItem from './LinkItem';
import { faviconUrl } from '../../../utils/functions';
import '../../../components/Sidebar/Sidebar.css';

const Files = ({ isVertical = false, verticalId }) => {
  const { user } = useContext(Context);
  const { isEditor } = useCategory();
  const canEdit = !isVertical || isEditor;
  const vqs = isVertical && verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '';

  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [editLinkItem, setEditLinkItem] = useState(null);

  const selectedFolder = folders.find((f) => f._id === selectedFolderId) || null;
  const selectedLinks = selectedFolder?.links || [];

  const handleOpenLinkModal = (item = null) => {
    setEditLinkItem(item);
    setOpenLinkModal(true);
  };

  const handleSaveLink = (updatedItem) => {
    if (selectedFolderId && updatedItem) {
      setFolders((prev) =>
        prev.map((f) => {
          if (f._id !== selectedFolderId) return f;
          const links = f.links || [];
          const isEdit = editLinkItem && editLinkItem._id;
          const nextLinks = isEdit
            ? links.map((l) =>
                l._id === editLinkItem._id ? { ...l, ...updatedItem, _id: editLinkItem._id } : l
              )
            : [...links, updatedItem];
          return { ...f, links: nextLinks };
        })
      );
    }
    setOpenLinkModal(false);
  };

  const handleFileDragEnd = async (result) => {
    if (!result.destination || !selectedFolderId) return;
    const updated = Array.from(selectedLinks);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    setFolders((prev) =>
      prev.map((f) => (f._id === selectedFolderId ? { ...f, links: updated } : f))
    );

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}update_file_order${vqs}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder_id: selectedFolderId,
          file_ids: updated.map((f) => f._id),
        }),
      });
    } catch (error) {
      console.error('Failed to update file order:', error);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!selectedFolderId || !file?._id) return;
    const prevFolders = folders;
    setFolders((prev) =>
      prev.map((f) =>
        f._id === selectedFolderId
          ? { ...f, links: (f.links || []).filter((l) => l._id !== file._id) }
          : f
      )
    );
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}delete_file/${
          file._id
        }?folder_id=${encodeURIComponent(selectedFolderId)}${
          isVertical && verticalId ? `&vertical_id=${encodeURIComponent(verticalId)}` : ''
        }`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('delete_file failed');
    } catch (error) {
      console.error('Failed to delete file:', error);
      setFolders(prevFolders);
    }
  };

  const persistOrder = async (orderedFolders) => {
    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}update_folder_order${vqs}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderedFolders.map((f) => f._id)),
      });
    } catch (error) {
      console.error('Failed to update folder order:', error);
    }
  };

  const handleSave = (updatedItem) => {
    const name = updatedItem?.folder_name;
    const id = updatedItem?._id;
    if (editItem) {
      setFolders((prev) => prev.map((f) => (f._id === id ? { ...f, name } : f)));
    } else {
      setFolders((prev) => {
        const newFolder = {
          _id: id,
          name,
          order: 0,
          links: [],
          created_at: updatedItem?.created_at || new Date().toISOString(),
        };
        const next = [newFolder, ...prev];
        persistOrder(next);
        return next;
      });
      setSelectedFolderId(id);
    }
    setOpenModal(false);
  };

  const handleDelete = async (folderId) => {
    const prevFolders = folders;
    const remaining = folders.filter((f) => f._id !== folderId);
    setFolders(remaining);
    if (selectedFolderId === folderId) {
      setSelectedFolderId(remaining[0]?._id || null);
    }
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}delete_folder/${folderId}${vqs}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('delete_folder failed');
    } catch (error) {
      console.error('Failed to delete folder:', error);
      setFolders(prevFolders);
      if (selectedFolderId === folderId) setSelectedFolderId(folderId);
    }
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setOpenModal(true);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const updated = Array.from(folders);
    const [moved] = updated.splice(result.source.index, 1);
    updated.splice(result.destination.index, 0, moved);

    setFolders(updated);
    persistOrder(updated);
  };

  useEffect(() => {
    fetchFolders();
  }, [user?.uid, isVertical, verticalId]);

  const fetchFolders = async () => {
    if (!user?.uid) return;
    setIsLoading(true);
    try {
      const token = await user.getIdToken();
      const url =
        process.env.REACT_APP_FLASK_WEBSERVER +
        'get_folders' +
        (isVertical && verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const list = (data.data?.folders || [])
        .slice()
        .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
      setFolders(list);
      setSelectedFolderId((prev) =>
        prev && list.some((f) => f._id === prev) ? prev : list[0]?._id || null
      );
    } catch (error) {
      console.error('Error fetching folders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        flex: 1,
        height: '100%',
        minHeight: 0,
        marginBottom: '16px',
      }}
    >
      <div
        className="workbench-card"
        style={{ padding: '12px', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <p style={{ color: 'gray', fontWeight: 600, fontSize: '14px' }}>
            {isVertical ? 'VERTICAL FILES' : 'MY FILES'}
          </p>
          {canEdit && (
            <IconButton color="inherit" onClick={() => handleOpenModal()} size="small">
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          )}
        </div>
        {!isLoading ? (
          <div
            style={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              textAlign: 'left',
              overflowY: 'auto',
              paddingRight: '8px',
            }}
          >
            {folders.length === 0 ? (
              <EmptyState
                imageSrc="/folder.svg"
                type="folder"
                onAddClick={canEdit ? () => handleOpenModal() : undefined}
              />
            ) : (
              <DragDropContext onDragEnd={canEdit ? handleDragEnd : () => {}}>
                <Droppable droppableId="folders" isDropDisabled={!canEdit}>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {folders.map((folder, index) => {
                        const isActive = selectedFolderId === folder._id;
                        return (
                          <Draggable
                            key={folder._id}
                            draggableId={folder._id}
                            index={index}
                            isDragDisabled={!canEdit}
                          >
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...(canEdit ? dragProvided.dragHandleProps : {})}
                                onClick={() => setSelectedFolderId(folder._id)}
                                className={isActive ? 'sidebar-item-active' : 'sidebar-item'}
                                style={{
                                  justifyContent: 'space-between',
                                  ...dragProvided.draggableProps.style,
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Folder
                                    sx={{
                                      color: isActive ? '#006ed4' : 'gray',
                                      fontSize: '36px',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    title={folder.name}
                                    style={{
                                      color: isActive ? '#006ed4' : 'inherit',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      minWidth: 0,
                                    }}
                                  >
                                    {folder.name}
                                  </span>
                                </div>
                                {canEdit && (
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <BasicMenu
                                      deleteHandler={() => handleDelete(folder._id)}
                                      editHandler={() =>
                                        handleOpenModal({
                                          _id: folder._id,
                                          title: folder.name,
                                          folder_id: folder._id,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        ) : (
          <div style={{ padding: '16px', marginTop: '50%', textAlign: 'center' }}>
            <CircularProgress size={24} style={{ color: '#000' }} />
          </div>
        )}
      </div>
      <div
        className="workbench-card"
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {!isLoading && folders.length > 0 && selectedFolder && (
        <>
        <div
          style={{
            backgroundColor: '#deeaff',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Folder
              sx={{
                color: '#006ed4',
                fontSize: '36px',
                flexShrink: 0,
              }}
            />
            <p style={{ color: '#006ed4', fontWeight: 700, fontSize: '24px', margin: '12px' }}>
              {selectedFolder.name}
            </p>
          </div>
          {canEdit && (
            <IconButton color="inherit" onClick={() => handleOpenLinkModal()} size="small">
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          )}
        </div>
        <div style={{ padding: '12px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {selectedLinks.length === 0 ? (
            <EmptyState
              imageSrc="/link.svg"
              type="file"
              onAddClick={canEdit ? () => handleOpenLinkModal() : undefined}
            />
          ) : (
            <DragDropContext onDragEnd={canEdit ? handleFileDragEnd : () => {}}>
              <Droppable droppableId="files" isDropDisabled={!canEdit}>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {selectedLinks.map((file, index) => (
                      <Draggable
                        key={file._id}
                        draggableId={file._id}
                        index={index}
                        isDragDisabled={!canEdit}
                      >
                        {(dragProvided) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...(canEdit ? dragProvided.dragHandleProps : {})}
                            style={{ ...dragProvided.draggableProps.style }}
                          >
                            <LinkItem
                              id={file._id}
                              title={file.title}
                              link={file.link}
                              icon={faviconUrl(file.link)}
                              deleteHandler={() => handleDeleteFile(file)}
                              editHandler={() => handleOpenLinkModal(file)}
                              canEdit={canEdit}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
        </>
        )}
      </div>
      <UnifiedModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        item={editItem}
        type="folder"
        onSave={handleSave}
        isVertical={isVertical}
        verticalId={verticalId}
        existingNames={folders
          .filter((f) => !editItem || f._id !== editItem._id)
          .map((f) => f.name)}
      />
      <UnifiedModal
        open={openLinkModal}
        handleClose={() => setOpenLinkModal(false)}
        item={editLinkItem}
        type="link"
        onSave={handleSaveLink}
        isVertical={isVertical}
        verticalId={verticalId}
        endpoint={
          editLinkItem ? `edit_file?id=${encodeURIComponent(editLinkItem._id)}` : 'add_file'
        }
        extraPayload={{ folder_id: selectedFolderId }}
      />
    </div>
  );
};
export default Files;
